// Stage moves — the UI half. The pure logic (visit math, the Undo patch)
// lives in stage_moves_logic.jsx as window.StageMovesLogic.
//
//   window.StageVisitTip — the stage timeline's hover / focus card: days in
//     the stage, who moved it there, when, and why (StageRail, deal_detail.jsx).
//   window.StageUndo — the toast after a stage / hold / exit change, with an
//     Undo that PATCHes the deal back through the normal update path. While
//     the toast is up, ⌘Z / Ctrl+Z outside a text field runs the same Undo.

const STAGE_UNDO_MS = 6000;

const fmtVisitDate = (v) => {
  if (!v) return null;
  const d = new Date(String(v).length === 10 ? `${v}T00:00:00` : v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: d.getFullYear() === new Date().getFullYear() ? undefined : 'numeric' });
};
const fmtDays = (n) => (n == null ? null : n === 0 ? 'Under a day' : n === 1 ? '1 day' : `${n.toLocaleString()} days`);

// Portal card anchored under a stage chip (the rail clips and scrolls, so the
// card can't live inside it). `id` ties it to the chip's aria-describedby.
const StageVisitTip = ({ id, anchor, stageName, visit, width = 260 }) => {
  const [pos, setPos] = React.useState(null);
  React.useLayoutEffect(() => {
    if (!anchor) { setPos(null); return; }
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const left = Math.max(8, Math.min(anchor.left, vw - width - 8));
    const below = anchor.bottom + 6;
    setPos(below + 150 > vh ? { left, bottom: vh - anchor.top + 6 } : { left, top: below });
  }, [anchor && anchor.left, anchor && anchor.top, width]);   // eslint-disable-line react-hooks/exhaustive-deps
  if (!anchor || !pos || !visit) return null;
  const v = visit;
  const muted = { color: 'var(--text-3)' };
  const row = (label, value) => value ? (
    <div style={{ display: 'flex', gap: 8, minWidth: 0 }}>
      <span style={{ ...muted, width: 62, flexShrink: 0, whiteSpace: 'nowrap' }}>{label}</span>
      <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere', color: 'var(--text)' }}>{value}</span>
    </div>
  ) : null;
  const who = (a) => (a ? [a.by, fmtVisitDate(a.at)].filter(Boolean).join(' · ') : null);
  let body;
  if (v.source === 'skipped') {
    body = <div style={muted}>Skipped</div>;
  } else {
    const days = fmtDays(v.days);
    body = (
      <>
        {days && (
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 6 }}>
            <span className="num" style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{days}</span>
            <span style={muted}>{v.current && !v.exit ? 'so far' : 'here'}</span>
            {v.visits > 1 && <span style={{ ...muted, marginLeft: 'auto' }}>{v.visits} visits</span>}
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {v.source === 'atlas'
            ? row('Moved by', who(v.entered) || 'Unknown')
            : row('Moved', [v.source === 'dealcloud' ? 'In DealCloud' : 'Before Atlas', fmtVisitDate(v.since)].filter(Boolean).join(' · '))}
          {row('Reason', v.reason)}
          {v.exit && row('Exited', [v.exit.name, who(v.exit)].filter(Boolean).join(' · '))}
          {v.exit && row('', v.exit.reason)}
          {v.left && row('Left', who(v.left))}
        </div>
      </>
    );
  }
  return ReactDOM.createPortal(
    <div id={id} role="tooltip" style={{
      position: 'fixed', ...pos, zIndex: 2000, width, maxWidth: 'calc(100vw - 16px)', boxSizing: 'border-box',
      padding: '9px 12px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
      boxShadow: 'var(--shadow-lg)', fontSize: 12, lineHeight: 1.5, color: 'var(--text-2)', pointerEvents: 'none',
    }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{stageName}</div>
      {body}
    </div>,
    document.body,
  );
};

// ── Undo ─────────────────────────────────────────────────────────────────
// Only a deal's latest move can be undone: the patch is absolute, so an older
// toast would wipe out everything since. A newer move on the same deal
// retires the older toast; the server refuses a stale one too (undo_stale).
// ⌘Z undoes the newest offer, the way the toast stack reads (newest at the
// bottom, nearest the hand).
const __stageUndoByDeal = new Map();   // deal id → { toastId, run, until }
let __stageUndoLatest = null;
const __isTextTarget = (el) => !!el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName || ''));
if (typeof document !== 'undefined') {
  document.addEventListener('keydown', (e) => {
    const u = __stageUndoLatest;
    if (!u || Date.now() > u.until) return;
    if (!(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey || String(e.key).toLowerCase() !== 'z') return;
    if (__isTextTarget(e.target)) return;
    e.preventDefault();
    u.run();
  });
}

const __undoKey = () => (/Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '') ? '⌘Z' : 'Ctrl+Z');

// After a save: offer Undo when `before` → `after` is a stage / exit / hold
// change that can be put back. `undo(patch)` is the caller's save (it updates
// its own state and must not toast its own failure); returns false when
// there was nothing to offer.
const offerStageUndo = ({ id, name, before, after, undo, duration = STAGE_UNDO_MS }) => {
  const L = window.StageMovesLogic;
  const key = id || after.id || null;
  const prior = key ? __stageUndoByDeal.get(key) : null;
  if (prior) retire(key, prior);
  const patch = L.undoPatch(before, after);
  if (!patch) return false;
  const entry = { toastId: null, run: null, until: Date.now() + duration };
  entry.run = async () => {
    if (entry.done) return;
    entry.done = true;
    retire(key, entry);
    try {
      await undo(patch);
      window.pushToast({ type: 'success', title: L.restoredTitle(before), body: name || undefined });
    } catch (e) {
      window.pushToast({ type: 'error', title: 'Undo failed', body: L.undoErrorText(e) });
    }
  };
  entry.toastId = window.pushToast({
    type: 'success', title: L.moveTitle(before, after), body: name || undefined, duration,
    action: { label: 'Undo', title: `Undo (${__undoKey()})`, onClick: () => entry.run() },
  });
  if (key) __stageUndoByDeal.set(key, entry);
  __stageUndoLatest = entry;
  return true;
};
// Drop an offer: its toast goes, and it no longer answers ⌘Z.
function retire(key, entry) {
  entry.done = true;
  if (key && __stageUndoByDeal.get(key) === entry) __stageUndoByDeal.delete(key);
  if (__stageUndoLatest === entry) __stageUndoLatest = null;
  window.dismissToast(entry.toastId);
}

window.StageVisitTip = StageVisitTip;
window.StageUndo = { offer: offerStageUndo, LINGER_MS: STAGE_UNDO_MS };
